// ─── WhatsApp share card ──────────────────────────────────────────────────────
// Shown live in Settings → WhatsApp Message Templates and, when the "Image card"
// share mode is on, rendered off-screen and rasterised to a PNG that is handed to
// the platform share sheet instead of the plain text template.
//
// Deliberately hook-free with explicit hex colours: the node is also mounted in a
// detached React root (outside the I18n provider) so no context and no CSS theme
// variables are guaranteed to be reachable while it is captured.

export function WACard({ card }) {
  const data = card || {};
  const sections = (data.sections || []).filter(
    s => s && (s.image
      || (Array.isArray(s.items) && s.items.length > 0)
      || (s.value !== undefined && s.value !== null && String(s.value).trim() !== ''))
  );
  const rawName = String(data.partyName || '').trim();
  const initial = rawName ? rawName.charAt(0).toUpperCase() : '#';
  const amountColor = data.tone === 'credit' ? '#16a34a' : '#dc2626';
  const footerColor = data.footerTone === 'credit' ? '#16a34a' : data.footerTone === 'debit' ? '#dc2626' : '#0f172a';
  const hasFooter = !!(data.footerLabel && data.footerValue);
  const sectionLabel = { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', marginBottom: '5px' };

  return (
    <div dir="ltr" style={{ width: '400px', boxSizing: 'border-box', background: '#ffffff', borderRadius: '18px', overflow: 'hidden', border: '1px solid #e2e8f0', fontFamily: "'Segoe UI', system-ui, -apple-system, Roboto, 'Helvetica Neue', sans-serif", color: '#0f172a' }}>
      {/* Business header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 18px' }}>
        {data.businessLogo ? (
          <img src={data.businessLogo} alt="" style={{ width: '32px', height: '32px', borderRadius: '8px', objectFit: 'cover', background: '#f1f5f9', flexShrink: 0 }} />
        ) : (
          <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#0284c7', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '15px', flexShrink: 0 }}>
            {String(data.businessName || 'H').charAt(0).toUpperCase()}
          </div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '14.5px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{data.businessName || 'HisabFlow'}</div>
          {data.businessPhone ? <div style={{ fontSize: '11.5px', color: '#64748b' }}>{data.businessPhone}</div> : null}
        </div>
        <div style={{ fontSize: '9.5px', fontWeight: 800, textTransform: 'uppercase', color: '#0284c7', background: 'rgba(2,132,199,0.10)', borderRadius: '999px', padding: '5px 9px', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {data.heading || 'WhatsApp'}
        </div>
      </div>

      <div style={{ height: '1px', background: '#e2e8f0' }} />

      {/* Party, date and headline amount */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '16px 18px' }}>
        <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: '#e2e8f0', color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '17px', flexShrink: 0 }}>
          {initial}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '15.5px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rawName || '—'}</div>
          {data.dateTime ? <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>{data.dateTime}</div> : null}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '21px', fontWeight: 800, color: amountColor, lineHeight: 1.15 }}>{data.amount}</div>
          {data.amountLabel ? <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '2px' }}>{data.amountLabel}</div> : null}
        </div>
      </div>

      {/* Detail rows: description, payment method, attachments, invoice lines… */}
      {sections.map((s, i) => {
        const cols = s.cols || {};
        const thStyle = { fontSize: '9.5px', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8', padding: '7px 6px', border: 'none' };
        const tdStyle = { fontSize: '12.5px', color: '#1e293b', padding: '7px 6px', border: 'none', wordBreak: 'break-word', lineHeight: 1.35 };
        return (
          <div key={`${s.label || 'row'}-${i}`} style={{ padding: '13px 18px', borderTop: '1px solid #f1f5f9' }}>
            {s.rightAlign ? (
              // Summary rows (subtotal / discount / paid): the block keeps its
              // exact look — uppercase header with the amount right under it —
              // but both sit on the RIGHT side of the card.
              <>
                <div style={{ ...sectionLabel, textAlign: 'right' }}>{s.label}</div>
                <div style={{ fontSize: '13.5px', color: '#1e293b', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5, textAlign: 'right' }}>{s.value}</div>
              </>
            ) : (
              <>
                <div style={sectionLabel}>{s.label}</div>
                {s.image ? (
                  <img src={s.image} alt="" style={{ width: '84px', height: '84px', borderRadius: '10px', objectFit: 'cover', border: '1px solid #e2e8f0', display: 'block' }} />
                ) : s.items ? (
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th style={{ ...thStyle, textAlign: 'left' }}>{cols.description}</th>
                          {cols.bulkQty && <th style={{ ...thStyle, textAlign: 'center', width: cols.smallQty ? '38px' : '48px' }}>{cols.bulkQty}</th>}
                          {cols.smallQty && <th style={{ ...thStyle, textAlign: 'center', width: '38px' }}>{cols.smallQty}</th>}
                          {cols.bulkPrice && <th style={{ ...thStyle, textAlign: 'right', width: cols.smallPrice ? '62px' : '78px' }}>{cols.bulkPrice}</th>}
                          {cols.smallPrice && <th style={{ ...thStyle, textAlign: 'right', width: '62px' }}>{cols.smallPrice}</th>}
                          {!cols.bulkQty && (
                            <>
                              <th style={{ ...thStyle, textAlign: 'center', width: '40px' }}>{cols.qty}</th>
                              <th style={{ ...thStyle, textAlign: 'right', width: '78px' }}>{cols.price}</th>
                            </>
                          )}
                          <th style={{ ...thStyle, textAlign: 'right', width: (cols.bulkQty && cols.smallQty) ? '68px' : '82px' }}>{cols.amount}</th>
                          {cols.discount ? <th style={{ ...thStyle, textAlign: 'right', width: '64px' }}>{cols.discount}</th> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {(s.items || []).map((it, idx) => (
                          <tr key={`${it.name || 'item'}-${idx}`} style={{ borderTop: '1px solid #f1f5f9' }}>
                            <td style={tdStyle}>{it.name}</td>
                            {cols.bulkQty && <td style={{ ...tdStyle, textAlign: 'center', fontWeight: it.hasBulkQty ? 700 : 400 }}>{it.bulkQty ?? '-'}</td>}
                            {cols.smallQty && <td style={{ ...tdStyle, textAlign: 'center' }}>{it.smallQty ?? '-'}</td>}
                            {cols.bulkPrice && <td style={{ ...tdStyle, textAlign: 'right', fontSize: '11px' }}>{it.bulkPrice ?? '-'}</td>}
                            {cols.smallPrice && <td style={{ ...tdStyle, textAlign: 'right', fontSize: '11px' }}>{it.smallPrice ?? '-'}</td>}
                            {!cols.bulkQty && (
                              <>
                                <td style={{ ...tdStyle, textAlign: 'center' }}>{it.qty}</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>{it.price}</td>
                              </>
                            )}
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{it.amount}</td>
                            {cols.discount ? <td style={{ ...tdStyle, textAlign: 'right', color: '#0f766e' }}>{it.discount}</td> : null}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ fontSize: '13.5px', color: '#1e293b', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>{s.value}</div>
                )}
              </>
            )}
          </div>
        );
      })}

      {/* Summary row (running balance / total / status) */}
      {hasFooter ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '14px 18px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>{data.footerLabel}</div>
          <div style={{ fontSize: '15.5px', fontWeight: 800, color: footerColor }}>{data.footerValue}</div>
        </div>
      ) : null}

      {/* Branding */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '11px 18px', background: '#0f172a' }}>
        <span style={{ width: '13px', height: '13px', borderRadius: '4px', background: '#0284c7', display: 'inline-block' }} />
        <span style={{ fontSize: '11.5px', fontWeight: 700, color: '#e2e8f0' }}>{data.brandName || 'HisabFlow'}</span>
      </div>
    </div>
  );
}
